import React, { useState } from 'react'
import { Global } from '@emotion/core'
import { Link as GLink } from 'gatsby'
import { Flex, Box, Heading, Text, Link, css } from 'theme-ui'
import { FiLayers, FiXCircle, FiShoppingBag } from 'react-icons/fi'

const styles = {
  global: {
    body: {
      overflow: `hidden !important`
    }
  },
  panel: {
    '*': {
      fontFamily: `-apple-system, BlinkMacSystemFont,
        "Segoe UI", Roboto, Helvetica, Arial, sans-serif,
        "Apple Color Emoji", "Segoe UI Emoji",
        "Segoe UI Symbol" !important`
    },
    display: [`none`, null, `block`],
    position: `fixed`,
    top: 0,
    right: 0,
    transform: `translate(100%, 0)`,
    width: `90%`,
    maxWidth: `650px`,
    height: `100vh`,
    bg: `white`,
    transition: `all .5s cubic-bezier(.77,0,.175,1)`,
    zIndex: 99,
    open: {
      transform: `translate(0, 0)`,
      boxShadow: `0 0 45px 0 rgba(0,0,0,.25)`
    }
  },
  button: {
    position: `absolute`,
    left: 0,
    bottom: `20%`,
    transform: `translateX(-130px)`,
    borderRadius: `0.5rem 0 0 0.5rem`,
    boxShadow: `-1px 1px 5px 0 rgba(1,1,1,.05)`,
    bg: `white`,
    cursor: `pointer`,
    textTransform: `uppercase`,
    fontSize: 1,
    p: 3,
    pr: 5,
    '&, :visited': {
      textDecoration: `none`,
      color: '#718096',
      fontWeight: `bold`
    },
    '&, span': {
      transition: `
        transform .5s cubic-bezier(.77,0,.175,1),
        opacity .5s cubic-bezier(.77,0,.175,1),
        color .05s ease
      `
    },
    svg: {
      verticalAlign: `middle`,
      mr: 2
    },
    ':hover': {
      color: `#667eea`
    },
    '& + &': {
      transform: `translate(-130px, 120%)`
    },
    open: {
      boxShadow: `15px 0px 50px 0 rgba(0,0,0,.25)`,
      transform: `translate(-60px, 0)`,
      '& + &': {
        transform: `translate(-60px, 120%)`
      },
      span: {
        opacity: 0
      }
    }
  },
  panelKicker: {
    fontSize: 3,
    fontWeight: `bold`,
    background: t => `
      linear-gradient(
        45deg,
        #667eea 32.5%,
        #805ad5 50.5%)
    `,
    color: `#805ad5`,
    WebkitBackgroundClip: `text`,
    WebkitTextFillColor: `transparent`
  },
  panelTitle: {
    fontSize: 5,
    fontWeight: `bold`,
    color: `#2d3748`
  },
  panelTitleWrapper: {
    textAlign: `center`,
    mb: 5
  },
  panelContent: {
    position: `relative`,
    height: `100%`,
    zIndex: 2,
    bg: `white`,
    overflowY: `scroll`,
    p: 5
  },
  demos: {
    flexWrap: `wrap`,
    m: -3
  },
  panelItem: {
    flexBasis: `50%`,
    textAlign: `center`,
    p: 3,
    transition: `all .3s ease-in-out`,
    ':hover': {
      transform: `translate3d(0,-5px,0)`
    }
  },
  demoLink: {
    color: `#2d3748`,
    '&, :visited': {
      color: `#2d3748`,
      textDecoration: `none`
    }
  },
  demoImage: {
    maxHeight: `265px`,
    overflow: `hidden`,
    boxShadow: `0 0 35px rgb(40 42 54 / 18%)`,
    borderRadius: `5px`,
    img: {
      width: `100%`
    }
  },
  overlay: {
    position: `fixed`,
    top: 0,
    right: `100%`,
    size: `100%`,
    bg: `#222`,
    opacity: 0,
    transition: `opacity .5s cubic-bezier(.77,0,.175,1)`,
    zIndex: 90
  },
  overlayOpen: {
    right: 0,
    opacity: 0.9
  }
}

const PanelContent = ({ open, demos, kicker, title, handleClick }) =>
  open ? (
    <Box sx={styles.panelContent}>
      <Box sx={styles.panelTitleWrapper}>
        <Heading as='div' sx={styles.panelKicker}>
          {kicker}
        </Heading>
        <Heading as='div' sx={styles.panelTitle}>
          {title}
        </Heading>
      </Box>
      <Flex sx={styles.demos}>
        {demos.map((demo, index) => {
          const isExternalLink = demo.link.startsWith('http')
          const linkProps = {
            [isExternalLink ? 'href' : 'to']: demo.link
          }
          if (isExternalLink) linkProps.rel = 'noopener'

          return (
            <Box key={`item-${index}`} sx={styles.panelItem}>
              <Link
                as={isExternalLink ? 'a' : GLink}
                {...linkProps}
                title={demo.name}
                css={css(styles.demoLink)}
                onClick={handleClick}
                target={isExternalLink ? '_blank' : '_self'}
              >
                <Heading sx={{ fontSize: 2, pb: 3 }}>{demo.name}</Heading>
                <Box sx={styles.demoImage}>
                  <img src={demo.image} loading='lazy' alt={demo.name} />
                </Box>
              </Link>
            </Box>
          )
        })}
      </Flex>
    </Box>
  ) : null

const PanelButtons = ({ open, handleClick, buyLink }) => {
  return (
    <>
      {open && <Global styles={styles.global} />}
      <Box
        sx={{ ...styles.button, ...(open && styles.button.open) }}
        onClick={handleClick}
        alt='Demos'
      >
        {open ? <FiXCircle size={26} /> : <FiLayers size={26} />}
        <span>Demos</span>
      </Box>
      <Box
        as='a'
        href={buyLink}
        sx={{ ...styles.button, ...(open && styles.button.open) }}
        alt='Buy Now'
        target='_blank'
        rel='noreferrer'
      >
        <FiShoppingBag size={26} color='#75AF08' />
        <Text as='span'>Buy Now</Text>
      </Box>
    </>
  )
}

const Panel = props => {
  const [open, setOpen] = useState()
  const handleClick = () => setOpen(!open)

  return (
    <>
      <Box sx={{ ...styles.panel, ...(open && styles.panel.open) }}>
        <PanelContent {...props} open={open} handleClick={handleClick} />
        <PanelButtons {...props} open={open} handleClick={handleClick} />
      </Box>
      <Box
        sx={{ ...styles.overlay, ...(open && styles.overlayOpen) }}
        onClick={handleClick}
      />
    </>
  )
}

export default Panel
